// Vivid360 v2.4 shared anatomies for auth. Path C-ish (vanilla + hand-rolled icons).
// One canonical export per anatomy.
import { forwardRef, useEffect, useId, useState } from 'react';
import { I } from '../../icons.jsx';

// ─── 7.1 Button ─────────────────────────────────────────────────────────────
const BUTTON_VARIANT = {
  primary:   'bg-violet-600 text-white hover:bg-violet-700 active:bg-violet-800 disabled:bg-violet-200 disabled:text-white',
  secondary: 'bg-white text-stone-800 border border-stone-300 hover:bg-stone-50 active:bg-stone-100',
  ghost:     'bg-transparent text-stone-700 hover:bg-stone-100 active:bg-stone-200',
  danger:    'bg-danger-600 text-white hover:bg-[#B91C1C] active:bg-[#991B1B]',
  link:      'bg-transparent text-violet-700 hover:text-violet-800 hover:underline',
};
const BUTTON_SIZE = {
  sm: 'h-7 px-3 text-sm',
  md: 'h-9 px-4 text-sm',
  lg: 'h-11 px-5 text-base',
};
export const Button = forwardRef(function Button(
  { variant = 'primary', size = 'md', className = '', children, ...rest },
  ref,
) {
  return (
    <button
      ref={ref}
      className={`inline-flex items-center justify-center gap-2 rounded-md font-semibold transition-colors focus-visible:outline-none focus-visible:shadow-focus disabled:cursor-not-allowed ${BUTTON_SIZE[size]} ${BUTTON_VARIANT[variant]} ${className}`}
      {...rest}
    >
      {children}
    </button>
  );
});

// ─── 7.2 FloatingField ──────────────────────────────────────────────────────
export const FloatingField = forwardRef(function FloatingField(
  { label, helper, error, trailing, id, className = '', ...rest },
  ref,
) {
  const generatedId = useId();
  const fieldId = id ?? generatedId;
  const helperId = `${fieldId}-helper`;
  const errorId = `${fieldId}-error`;
  return (
    <div className="flex flex-col gap-1.5">
      <div className={`m-field relative inline-flex w-full ${error ? 'm-field--error' : ''} ${className}`}>
        <input
          ref={ref}
          id={fieldId}
          placeholder=" "
          aria-invalid={error ? true : undefined}
          aria-describedby={error ? errorId : helper ? helperId : undefined}
          className="m-field__input"
          {...rest}
        />
        <label htmlFor={fieldId} className="m-field__label">
          {label}
        </label>
        {trailing && <span className="absolute inset-y-0 end-3 flex items-center text-stone-500">{trailing}</span>}
      </div>
      {error ? (
        <span id={errorId} role="alert" className="ms-3.5 inline-flex items-center gap-1 text-xs text-danger-600">
          <I.AlertCircle size={14} /> {error}
        </span>
      ) : helper ? (
        <span id={helperId} className="ms-3.5 text-xs text-stone-500">
          {helper}
        </span>
      ) : null}
    </div>
  );
});

// ─── 7.28 Spinner ───────────────────────────────────────────────────────────
export function Spinner({ size = 40, label = 'Loading' }) {
  return (
    <span
      role="status"
      aria-live="polite"
      aria-label={label}
      aria-busy="true"
      style={{ width: size, height: size, display: 'inline-block' }}
    >
      <svg viewBox="0 0 40 40" width={size} height={size} className="animate-spin" aria-hidden>
        <circle cx="20" cy="20" r="16" stroke="currentColor" strokeOpacity="0.2" strokeWidth="3" fill="none" />
        <path d="M 20 4 a 16 16 0 0 1 16 16" stroke="currentColor" strokeWidth="3" strokeLinecap="round" fill="none" />
      </svg>
    </span>
  );
}

// ─── 7.27 Empty state ───────────────────────────────────────────────────────
export function EmptyState({ icon, heading, description, cta }) {
  return (
    <div className="flex flex-col items-center text-center px-6 py-12">
      <span className="text-stone-400 mb-4" aria-hidden>
        {icon}
      </span>
      <h3 className="text-base font-semibold text-stone-900">{heading}</h3>
      {description && <p className="mt-2 text-sm text-stone-600 max-w-[36ch]">{description}</p>}
      {cta && <div className="mt-4">{cta}</div>}
    </div>
  );
}

// ─── 7.26 Banner ────────────────────────────────────────────────────────────
export function Banner({ variant = 'info', icon, className = '', children, ...rest }) {
  return (
    <div role="status" className={`banner banner-${variant} flex items-center gap-2 ${className}`} {...rest}>
      {icon && <span className="shrink-0">{icon}</span>}
      <span className="flex-1">{children}</span>
    </div>
  );
}

// ─── 7.31 Step indicator (vertical) ─────────────────────────────────────────
export function StepIndicator({ steps }) {
  return (
    <ol className="flex flex-col gap-1" aria-label="Progress">
      {steps.map((s) => (
        <li
          key={s.id}
          aria-current={s.status === 'active' ? 'step' : undefined}
          className={`flex items-center gap-3 rounded-md px-3.5 py-2.5 ${
            s.status === 'active' ? 'bg-stone-100' : ''
          }`}
        >
          <span
            aria-hidden
            className={`inline-flex h-[18px] w-[18px] items-center justify-center rounded-full border-[1.5px] ${
              s.status === 'completed'
                ? 'bg-violet-600 border-violet-600 text-white'
                : s.status === 'active'
                  ? 'border-stone-900'
                  : 'border-stone-300'
            }`}
          >
            {s.status === 'completed' && <I.Check size={12} />}
          </span>
          <span
            className={`text-sm ${
              s.status === 'upcoming'
                ? 'text-stone-600'
                : s.status === 'active'
                  ? 'font-medium text-stone-900'
                  : 'text-stone-900'
            }`}
          >
            {s.label}
          </span>
        </li>
      ))}
    </ol>
  );
}

// ─── 8.1 Right-anchored Drawer ──────────────────────────────────────────────
export function Drawer({ open, onClose, title, dirty, footer, children }) {
  const titleId = useId();
  const [confirmDiscard, setConfirmDiscard] = useState(false);

  useEffect(() => {
    if (!open) return;
    const onKey = (e) => {
      if (e.key === 'Escape') requestClose();
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  });

  function requestClose() {
    if (dirty) setConfirmDiscard(true);
    else onClose();
  }

  if (!open) return null;
  return (
    <>
      <div className="fixed inset-0 bg-stone-900/[0.18] z-40" onClick={requestClose} aria-hidden />
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        className="fixed inset-y-0 end-0 z-50 flex w-full md:w-[380px] flex-col bg-white shadow-xl border-s border-stone-200 animate-drawer-in"
      >
        <header className="flex items-center justify-between px-[22px] py-[18px] border-b border-stone-200">
          <h2 id={titleId} className="text-[17px] font-semibold text-stone-900">
            {title}
          </h2>
          <button
            type="button"
            aria-label="Close"
            onClick={requestClose}
            className="inline-flex h-8 w-8 items-center justify-center rounded-md text-stone-600 hover:bg-stone-100 focus-visible:outline-none focus-visible:shadow-focus"
          >
            <svg viewBox="0 0 24 24" width={20} height={20} fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden><path d="M18 6 6 18M6 6l12 12" /></svg>
          </button>
        </header>
        <div className="flex-1 overflow-y-auto px-[22px] py-5 space-y-[18px]">{children}</div>
        {footer && (
          <footer className="border-t border-stone-200 bg-white px-[22px] py-[14px] flex items-center justify-end gap-2">
            {footer}
          </footer>
        )}
      </div>
    </>
  );
}

// ─── 7.9 Badge ──────────────────────────────────────────────────────────────
const BADGE_VARIANT = {
  neutral: 'bg-stone-100 text-stone-700',
  accent:  'bg-violet-50 text-violet-700',
  success: 'bg-[#D1FAE5] text-[#065F46]',
  warning: 'bg-[#FEF3C7] text-[#92400E]',
  danger:  'bg-[#FEE2E2] text-[#991B1B]',
};
export function Badge({ variant = 'neutral', dot, className = '', children, ...rest }) {
  return (
    <span
      className={`inline-flex items-center gap-1.5 h-5 px-2 rounded-sm text-xs font-medium ${BADGE_VARIANT[variant]} ${className}`}
      {...rest}
    >
      {dot && <span className="w-2 h-2 rounded-full bg-current opacity-70" aria-hidden />}
      {children}
    </span>
  );
}
